import { PageHeader, PaginationPageDefault, TableCard } from "@components";
import { Button } from "@components/button/button-untitledui";
import { usePaginationTotal } from "@components/pagination/use-pagination-total";
import { Download01 } from "@untitledui/icons";
import { useCallback, useMemo, useState } from "react";
import type { SortDescriptor } from "react-aria-components";
import { toast } from "sonner";
import { useGroupsQuery } from "@/shared/hooks";
import { useDebouncedCommit } from "@/shared/hooks/use-debounced-commit";
import {
	CommunicationFeedbackState,
	CommunicationFilters,
	type CommunicationFiltersState,
	CommunicationStatusTabs,
	createDefaultCommunicationFilters,
	ListingDataTable,
} from "../components";
import {
	COMMUNICATION_PAGE_SIZE,
	COMMUNICATION_TABLE_COLUMNS,
} from "../constants";
import { useCommunicationList, useCommunicationStatusCount } from "../hooks";
import { useExportCommunication } from "../query";
import type {
	CommunicationExportParams,
	CommunicationListParams,
	CommunicationStatusTab,
} from "../types";
import {
	communicationExportSuffix,
	communicationStatusTabToApi,
	exportCommunicationXlsx,
	getCommunicationRowKey,
	renderCommunicationCell,
} from "../utils";

function toCommunicationListParams(
	filters: CommunicationFiltersState,
	statusTab: CommunicationStatusTab,
	page: number,
	sortDescriptor: SortDescriptor | undefined
): CommunicationListParams {
	const statusFilter = communicationStatusTabToApi(statusTab);

	const params: CommunicationListParams = {
		pageNumber: page,
		pageSize: COMMUNICATION_PAGE_SIZE,
	};

	if (filters.search.trim()) {
		params.search = filters.search.trim();
	}

	if (filters.groupIds.length > 0) {
		params.groupIds = filters.groupIds;
	}

	if (statusFilter !== undefined) {
		params.status = statusFilter;
	}

	if (sortDescriptor) {
		params.sortBy = String(sortDescriptor.column);
		params.sortDirection =
			sortDescriptor.direction === "descending" ? "desc" : "asc";
	}

	return params;
}

const SEARCH_DEBOUNCE_MS = 500;

const normalizeSearch = (value: string) => value.trim();

function toExportParams(
	params: CommunicationListParams
): CommunicationExportParams {
	return {
		search: params.search,
		groupIds: params.groupIds,
		status: params.status,
	};
}

export default function TrackingCommunicationPage() {
	const [page, setPage] = useState(1);
	const [filters, setFilters] = useState<CommunicationFiltersState>(
		createDefaultCommunicationFilters
	);
	const [searchInput, setSearchInput] = useState("");
	const [statusTab, setStatusTab] = useState<CommunicationStatusTab>("all");
	const [sortDescriptor, setSortDescriptor] = useState<
		SortDescriptor | undefined
	>();

	const { data: groups = [] } = useGroupsQuery();

	const statusCountFilters = useMemo(
		() => ({
			search: filters.search.trim() || undefined,
			groupIds: filters.groupIds.length > 0 ? filters.groupIds : undefined,
		}),
		[filters.search, filters.groupIds]
	);

	const { data: tabCounts, isLoading: isTabCountsLoading } =
		useCommunicationStatusCount(statusCountFilters);
	const { mutate: exportCommunication, isPending: isExporting } =
		useExportCommunication();

	const handleClear = useCallback(() => {
		setSearchInput("");
		setFilters(createDefaultCommunicationFilters());
		setStatusTab("all");
		setPage(1);
	}, []);

	const handleFiltersChange = useCallback((next: CommunicationFiltersState) => {
		setFilters(next);
	}, []);

	const applySearch = useCallback((term: string) => {
		setFilters((prev) => ({ ...prev, search: term }));
		setPage(1);
	}, []);

	useDebouncedCommit({
		value: searchInput,
		applied: filters.search,
		delay: SEARCH_DEBOUNCE_MS,
		normalize: normalizeSearch,
		onCommit: applySearch,
	});

	const handleSortChange = useCallback((descriptor: SortDescriptor) => {
		setSortDescriptor(descriptor);
		setPage(1);
	}, []);

	const queryParams = useMemo(
		() => toCommunicationListParams(filters, statusTab, page, sortDescriptor),
		[filters, statusTab, page, sortDescriptor]
	);

	const criteria = useMemo(() => {
		const { pageNumber: _pageNumber, ...rest } = queryParams;
		return rest;
	}, [queryParams]);

	const communicationQuery = useCommunicationList(queryParams);
	const { data, isLoading, isError, refetch } = communicationQuery;

	const { total: totalPages } = usePaginationTotal({
		page,
		onPageChange: setPage,
		criteria,
		totalPages: data?.totalPages,
		query: communicationQuery,
	});

	const items = data?.items ?? [];
	const showPagination = totalPages !== null && totalPages > 1;
	const totalCount = data?.totalCount ?? 0;
	const canExport = !(isLoading || isError) && totalCount > 0;
	const hasSearch = filters.search.trim().length > 0;

	const handleExport = useCallback(() => {
		if (!canExport || isExporting) {
			return;
		}

		exportCommunication(toExportParams(queryParams), {
			onSuccess: (exportItems) => {
				if (exportItems.length === 0) {
					toast.error("Nenhum dado disponível para exportação.");
					return;
				}

				exportCommunicationXlsx(
					exportItems,
					communicationExportSuffix(statusTab)
				);
			},
			onError: () => {
				toast.error(
					"Não foi possível exportar os dados. Tente novamente em instantes."
				);
			},
		});
	}, [canExport, exportCommunication, isExporting, queryParams, statusTab]);

	const handleStatusTabChange = useCallback((tab: CommunicationStatusTab) => {
		setStatusTab(tab);
		setPage(1);
	}, []);

	const statusTabCounts = tabCounts ?? {
		all: 0,
		connected: 0,
		disconnected: 0,
	};

	return (
		<div className="flex min-h-full flex-col gap-6 px-6 py-8">
			<PageHeader title="Comunicação" />

			<CommunicationFilters
				actions={
					<Button
						color="secondary"
						iconLeading={Download01}
						isDisabled={!canExport || isExporting}
						onClick={handleExport}
						size="sm"
					>
						{isExporting ? "Exportando..." : "Exportar dados"}
					</Button>
				}
				filters={filters}
				groups={groups}
				onFiltersChange={handleFiltersChange}
				onPageChange={setPage}
				onSearchInputChange={setSearchInput}
				searchInput={searchInput}
			/>

			<CommunicationStatusTabs
				activeTab={statusTab}
				counts={statusTabCounts}
				isCountsLoading={isTabCountsLoading}
				onTabChange={handleStatusTabChange}
			/>

			<TableCard.Root variant="listing">
				<ListingDataTable
					ariaLabel="Lista de última comunicação dos veículos"
					columns={COMMUNICATION_TABLE_COLUMNS}
					emptyContent={
						<CommunicationFeedbackState
							hasSearch={hasSearch}
							onClearFilters={handleClear}
							variant="empty"
						/>
					}
					errorContent={
						<CommunicationFeedbackState
							onRetry={() => {
								refetch();
							}}
							variant="error"
						/>
					}
					fixedLayout
					getRowKey={getCommunicationRowKey}
					isError={isError}
					isLoading={isLoading}
					items={items}
					loadingLabel="Carregando veículos"
					onRetry={() => {
						refetch();
					}}
					onSortChange={handleSortChange}
					renderCell={(item, column) => renderCommunicationCell(item, column)}
					sortDescriptor={sortDescriptor}
					variant="listing"
				/>
				{showPagination ? (
					<PaginationPageDefault
						className="p-5"
						onPageChange={setPage}
						page={page}
						total={totalPages}
					/>
				) : null}
			</TableCard.Root>
		</div>
	);
}
